import React, { useEffect, useState } from "react";
import KpiCard from "../../CommonComponents/Dashboard/KpiCard/KpiCard";
import { KPI_CARD_BACKGROUND } from "../../../utils/Constants";

import "./stylesProviderPerformance.css";

import { Line } from "react-chartjs-2";
import { formatNumber } from "chart.js/helpers";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend,
  PointElement,
  LineElement,
} from "chart.js";
import { Skeleton } from "antd";
import { useRef } from "react";

function ProviderPerformance({
  isApiRunning,
  title,
  showBottomKpis = true,
  providerData,
  totalEvents,
}) {
  const [hoveredValue, setHoveredValue] = useState(null);
  const chartRef = useRef(null);
  ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend
  );

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      title: { display: false },
      tooltip: {
        enabled: true,
        mode: "nearest",
        intersect: false,
        callbacks: {
          label: function (context) {
            const value = context.raw;
            return `${context.dataset.label}: ${formatNumber(
              value,
              "en-US",
              "decimal",
              2
            )}\n`;
          },
        },
        backgroundColor: "#25252F",
        titleFont: { family: "Poppins-regular", size: 16, weight: "bold" },
        bodyFont: { family: "Poppins-regular", size: 14 },
        padding: 20,
        displayColors: true,
      },
      datalabels: { display: false },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function (value) {
            if (value >= 1000) {
              return value / 1000 + "k";
            } else {
              return value;
            }
          },
        },
      },
    },
    interaction: {
      mode: "index",
      intersect: false,
      axis: "x",
    },
    onHover: (event, chartElement) => {
      if (chartElement.length > 0) {
        const index = chartElement[0].index;
        setHoveredValue(values[index] || 0);
      }
    },
  };

  const handleSelectedProvider = () => {
    if (isApiRunning) {
      return "Loading...";
    }
    return title || "All Providers";
  };

  const totalEventsByDay = Object.entries(totalEvents?.data || {}).map(
    ([date, event_count]) => ({
      date,
      event_count,
    })
  );
  const labels = totalEventsByDay.map((item) => item.date);
  const values = totalEventsByDay.map((item) => Number(item.event_count));

  const data = {
    labels,
    datasets: [
      {
        label: "kpi",
        data: values,
        borderColor: "#FD6F27",
        backgroundColor: "#FD6F27",
        borderRadius: 1,
        pointRadius: 0,
        pointHoverRadius: 0,
      },
    ],
  };

  useEffect(() => {
    if (values.length > 0) {
      setHoveredValue(totalEvents?.total_events || 0);
    }
  }, [providerData]);

  useEffect(() => {
    const chart = chartRef.current;
    const canvas = chart?.canvas;
  
    if (!canvas) return;
  
    const handleMouseLeave = () => {
      setHoveredValue(totalEvents?.total_events || 0);
    };
  
    canvas.addEventListener("mouseleave", handleMouseLeave);
  
    return () => {
      canvas.removeEventListener("mouseleave", handleMouseLeave);
    };
  }, [totalEvents]);

  return (
    <div className="provider-performance-container">
      <div className="provider-performance-body">
        <div className="provider-performance-top">
          <KpiCard
            isApiRunning={isApiRunning}
            title={"Profit & Loss"}
            value={Number(providerData?.ggr || 0)}
            hugeValue={false}
            rate={false}
            percentage={false}
            backgroundColor={KPI_CARD_BACKGROUND.LIGHT}
          />
          <KpiCard
            isApiRunning={isApiRunning}
            title={"Margin"}
            value={Number(providerData?.margin || 0)}
            hugeValue={false}
            rate={true}
            percentage={false}
            backgroundColor={KPI_CARD_BACKGROUND.LIGHT}
          />
          <KpiCard
            isApiRunning={isApiRunning}
            title={"Cash Out Profit and Loss"}
            value={Number(providerData?.cash_out_profit || 0)}
            customColor={false}
            hugeValue={false}
            percentage={false}
            backgroundColor={KPI_CARD_BACKGROUND.LIGHT}
          />
          <KpiCard
            isApiRunning={isApiRunning}
            title="Turnover"
            value={Number(providerData?.turnover || 0)}
            hugeValue={true}
            percentage={false}
            backgroundColor={KPI_CARD_BACKGROUND.TRASNPARENT}
          />
        </div>

        <div className="provider-performance-middle">
          <div className="provider-performance-top-left-container">
            <div className="provider-performance-top-left-content graph-container">
              {isApiRunning ? (
                <div
                  style={{
                    width: "100%",
                    height: "300px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Skeleton active />
                </div>
              ) : (
                <>
                  <h3>TOTAL EVENTS</h3>

                  <div className="principal-kpi">
                    <p className="kpi">
                      {Number(hoveredValue).toLocaleString("en-US")}
                    </p>
                  </div>
                  <div style={{ height: "200px", width: "100%" }}>
                    <Line data={data} options={options} ref={chartRef} />
                  </div>
                </>
              )}
            </div>
          </div>
          <div className="provider-performance-top-right-content">
            <div className="right-content-top">
              <KpiCard
                isApiRunning={isApiRunning}
                title={"Future"}
                value={Number(totalEvents?.future_events || 0)}
                hugeValue={false}
                rate={false}
                percentage={false}
                maximumFractionDigits={0}
                minimumFractionDigits={0}
                backgroundColor={KPI_CARD_BACKGROUND.LIGHT}
              />
              <KpiCard
                isApiRunning={isApiRunning}
                title={"Live"}
                value={Number(totalEvents?.live_events || 0)}
                hugeValue={false}
                percentage={false}
                maximumFractionDigits={0}
                minimumFractionDigits={0}
                backgroundColor={KPI_CARD_BACKGROUND.LIGHT}
              />
            </div>
            <div className="right-content-bottom">
              <KpiCard
                isApiRunning={isApiRunning}
                title={"Completed"}
                value={Number(totalEvents?.completed_events || 0)}
                maximumFractionDigits={0}
                minimumFractionDigits={0}
                hugeValue={false}
                percentage={false}
                backgroundColor={KPI_CARD_BACKGROUND.LIGHT}
              />
            </div>
          </div>
        </div>
        <div className="provider-performance-bottom">
          <KpiCard
            isApiRunning={isApiRunning}
            title={"TOTAL BETS"}
            value={providerData?.total_bets || 0}
            maximumFractionDigits={0}
            minimumFractionDigits={0}
            hugeValue={false}
            percentage={false}
            backgroundColor={KPI_CARD_BACKGROUND.DARK}
          />
          <KpiCard
            isApiRunning={isApiRunning}
            title={"Avg Bet Amount"}
            value={Number(providerData?.avg_bet_amount || 0)}
            hugeValue={false}
            percentage={false}
            backgroundColor={KPI_CARD_BACKGROUND.DARK}
          />
          <KpiCard
            isApiRunning={isApiRunning}
            title="Players Engaged"
            value={providerData?.num_players_engaged || 0}
            maximumFractionDigits={0}
            hugeValue={false}
            percentage={false}
            backgroundColor={KPI_CARD_BACKGROUND.DARK}
          />
        </div>
      </div>
    </div>
  );
}

export default ProviderPerformance;
